import { Component, type ErrorInfo, type ReactNode } from "react"
import { RefreshCw, TriangleAlert } from "lucide-react"
import { Button } from "@/components/ui/button"

type ErrorBoundaryProps = {
  children: ReactNode
  description?: string
}

type ErrorBoundaryState = {
  error: Error | null
}

/**
 * Rede de segurança de renderização: sem um boundary, uma exceção em qualquer
 * componente do editor desmonta a árvore inteira e deixa a tela em branco.
 * Em vez disso, mostramos uma mensagem em PT-BR e um botão para recarregar.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] Falha ao renderizar o editor.", error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      return (
        <div role="alert" className="bg-background text-foreground flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
          <span className="flex size-12 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
            <TriangleAlert className="size-6" />
          </span>
          <h1 className="font-display text-xl font-bold">Não foi possível exibir esta página</h1>
          <p className="text-muted-foreground max-w-md text-xs leading-relaxed">
            {this.props.description ?? "Ocorreu um erro inesperado ao renderizar o conteúdo. Recarregue a página para tentar novamente."}
          </p>
          <Button size="sm" onClick={() => window.location.reload()}>
            <RefreshCw className="mr-1.5 size-3.5" />Recarregar
          </Button>
        </div>
      )
    }
    return this.props.children
  }
}
